import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createBrowserRouter, RouterProvider, ScrollRestoration, Outlet } from 'react-router-dom';
import useAuthStore from './store/useAuthStore';
import { Command } from '@tauri-apps/plugin-shell';
import DeviceRegistrationModal from './components/DeviceRegistrationModal';
import GlobalVibeAlert from './components/chat/GlobalVibeAlert';
import UpdateChecker from './components/UpdateChecker';

// Pages
import DesktopLogin from './pages/DesktopLogin';
import PrivateRoute from './components/PrivateRoute';
import TestListPage from './pages/TestListPage';
import TestDetailPage from './pages/TestDetailPage';

import ExpertMarketplace from './pages/ExpertMarketplace';
import DevicesPage from './pages/DevicesPage';
import SidebarLayout from './components/SidebarLayout';
import HomePage from './pages/HomePage';
import SettingsPage from './pages/SettingsPage';
import ContactsPage from './pages/ContactsPage';
import NodeList from './components/NodeList';
import ChatRoomPage from './pages/ChatRoomPage';
import NodeDashboardPage from './pages/NodeDashboardPage';
import OnboardingPage from './pages/OnboardingPage';
import TasksPage from './pages/TasksPage';
import HistoryPage from './pages/HistoryPage';
import LandingPage from './pages/LandingPage';

// Root Layout to handle ScrollRestoration
const RootLayout = () => (
  <>
    <GlobalVibeAlert />
    <UpdateChecker />
    <ScrollRestoration />
    <Outlet />
  </>
);

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    const unsubscribe = initializeAuth();
    return () => unsubscribe();
  }, [initializeAuth]);

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [pendingDeviceName, setPendingDeviceName] = useState('');

  const isTauriDesktop = !!window.__TAURI_INTERNALS__;
  const sidecarRef = useRef(null);
  const spawningRef = useRef(false);
  const [sidecarStatus, setSidecarStatus] = useState('idle'); // idle, spawning, online, error
  const [sidecarError, setSidecarError] = useState(null);

  // Spawns the Python sidecar and optionally registers the device
  const spawnSidecar = useCallback(async (registerDevice = true) => {
    if (!user || !isTauriDesktop || spawningRef.current || sidecarRef.current) return;
    
    try {
      spawningRef.current = true;
      const isLocalhost = process.env.NODE_ENV === 'development';
      console.log(`Spawning Python sidecar for user ${user.uid} (Emulator: ${isLocalhost}, Register: ${registerDevice})`);

      // Use the sidecar name defined in tauri.conf.json
      const command = Command.sidecar('bin/desktop_agent');

      command.stdout.on('data', line => {
        console.log(`[Python]: ${line}`);
        if (line.includes('__TAURI_DEVICE_ID__:')) {
          const deviceId = line.split('__TAURI_DEVICE_ID__:')[1].trim();
          useAuthStore.getState().setLocalDeviceId(deviceId);
          console.log("Local Device ID bound to UI:", deviceId);
        }
      });
      command.stderr.on('data', line => console.error(`[Python Err]: ${line}`));

      command.on('close', payload => {
        console.warn('Sidecar closed:', payload);
        setSidecarStatus('error');
        setSidecarError(`Closed: ${JSON.stringify(payload)}`);
        sidecarRef.current = null;
      });
      command.on('error', error => {
        console.error('Sidecar error:', error);
        setSidecarStatus('error');
        setSidecarError(error.toString());
      });

      const child = await command.spawn();
      sidecarRef.current = child; // Keep reference to prevent GC/Pipe closure
      setSidecarStatus('online');

      console.log("Sidecar spawned successfully, sending initialization payload...");
      await child.write(JSON.stringify({
        action: "set_uid",
        uid: user.uid,
        emulator: isLocalhost,
        registerDevice
      }) + "\n");
    } catch (err) {
      console.error("Failed to spawn sidecar:", err);
      setSidecarStatus('error');
      setSidecarError(err.toString());
    } finally {
      spawningRef.current = false;
    }
  }, [user, isTauriDesktop]);

  // Periodic ping to verify sidecar health
  useEffect(() => {
    let interval;
    if (sidecarRef.current) {
      interval = setInterval(async () => {
        try {
          await sidecarRef.current.write(JSON.stringify({ action: "ping" }) + "\n");
        } catch (err) {
          console.error("Sidecar connection lost during ping:", err);
          sidecarRef.current = null;
        }
      }, 20000); // every 20s
    }
    return () => clearInterval(interval);
  }, [user]);

  // Check if we need to show the registration modal
  useEffect(() => {
    if (user && isTauriDesktop) {
      const storageKey = `device_registered_${user.uid}`;
      const alreadyDecided = localStorage.getItem(storageKey);

      if (alreadyDecided === 'accepted') {
        // User previously accepted — spawn sidecar silently
        spawnSidecar(true);
      } else if (alreadyDecided === 'declined') {
        // User previously declined — spawn sidecar without device registration
        spawnSidecar(false);
      } else {
        // First time — show the modal
        try {
          const result = new XMLHttpRequest();
          result.open('GET', '', false); // dummy to not block
        } catch (e) { /* ignore */ }
        // Get device name for the modal
        setPendingDeviceName(window.navigator?.userAgent?.includes('Mac') ? 'Mac' : 'This Computer');
        setShowDeviceModal(true);
      }
    }
  }, [user, isTauriDesktop, spawnSidecar]);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <RootLayout />,
      children: [
        {
          // Global layout shell with Sidebar
          element: <SidebarLayout />,
          children: [
            {
              path: "/",
              element: user ? <HomePage /> : <LandingPage />,
            },
            {
              path: "/projects",
              element: (
                <PrivateRoute>
                  <NodeList />
                </PrivateRoute>
              ),
            },
            {
              path: "/settings",
              element: (
                <PrivateRoute>
                  <SettingsPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/tasks",
              element: (
                <PrivateRoute>
                  <TasksPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/agents",
              element: (
                <PrivateRoute>
                  <ExpertMarketplace />
                </PrivateRoute>
              ),
            },
            {
              path: "/devices",
              element: (
                <PrivateRoute>
                  <DevicesPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/experts",
              element: (
                <PrivateRoute>
                  <ExpertMarketplace />
                </PrivateRoute>
              ),
            },
            {
              path: "/space/:nodeId",
              element: (
                <PrivateRoute>
                  <NodeDashboardPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/chat/:chatId",
              element: (
                <PrivateRoute>
                  <ChatRoomPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/history",
              element: (
                <PrivateRoute>
                  <HistoryPage />
                </PrivateRoute>
              ),
            }
          ]
        },

        {
          path: "/onboarding",
          element: (
            <PrivateRoute>
              <OnboardingPage />
            </PrivateRoute>
          )
        },

        // Public / Independent Routes
        {
          path: "/login",
          element: <DesktopLogin />,
        },
        {
          path: "/test-list",
          element: <TestListPage />,
        },
        {
          path: "/test-detail/:id",
          element: <TestDetailPage />,
        },
      ]
    }
  ]);

  return (
    <div className="min-h-screen antialiased">
      {showDeviceModal && (
        <DeviceRegistrationModal
          deviceName={pendingDeviceName}
          onAccept={() => {
            const storageKey = `device_registered_${user?.uid}`;
            localStorage.setItem(storageKey, 'accepted');
            setShowDeviceModal(false);
            spawnSidecar(true);
          }}
          onDecline={() => {
            const storageKey = `device_registered_${user?.uid}`;
            localStorage.setItem(storageKey, 'declined');
            setShowDeviceModal(false);
            spawnSidecar(false);
          }}
        />
      )}
      <RouterProvider router={router} />
    </div>
  );
}

export default App;
